import axios, { type AxiosResponse } from "axios";
import Log from "../../../../../Logger";
import { getAccessToken } from "../../../../../auth";

export type AiUsageResponse = {
    aiServiceName: string;
    month: string;
    usageCount: number;
};

const GRAPHQL_API_URL = import.meta.env.VITE_GRAPHQL_API_URL ?? '';

type Input = {
    [key: string]: Input | string | number | boolean | Array<Input | string | number | boolean>
};

type GraphQLBody = any;

type AiUsageGraphQLResponse = {
    aiUsage: AiUsageResponse | null;
};

/**
 * レスポンスオブジェクトから "ricoh-ewb-request-id" ヘッダーの値を取得します。
 * ヘッダーが存在しない場合は "-" を返します。
 *
 * @param resp レスポンスオブジェクト。HTTPレスポンスのヘッダー情報を含みます。
 * 例: { headers: { "ricoh-ewb-request-id": "12345-abcde" } }
 * @returns "ricoh-ewb-request-id" ヘッダーの値、またはヘッダーが存在しない場合は "-"
 */
function getEwbReqId(resp: any) {
    return resp?.headers?.["ricoh-ewb-request-id"] ?? "-";
}

/**
 * 指定されたGraphQLクエリと変数を使用してAPIリクエストを送信し、レスポンスを返します。
 * アクセストークンの取得やリクエストのロギングも行います。
 *
 * @template T レスポンスデータの型
 * @param query GraphQLクエリ文字列 例: `query GetUser($id: ID!) { user(id: $id) { name email } }`
 * @param variables クエリに渡す変数のオブジェクト 例: `{ id: "12345" }`
 * @returns レスポンスデータとエラー情報を含むPromise
 */
function query<T>(query: string, variables: Input): Promise<AxiosResponse<{ data: T, errors?: any }, GraphQLBody>> {
    return (async () => {
        const logger = Log.getLogger();
        logger.api.info("Cognito", "Start to request: currentSession");
        const token = await getAccessToken();
        logger.api.info("Cognito", "Succeed to request: currentSession");

        const apiResponse: AxiosResponse<{ data: T, errors?: any }, GraphQLBody> = await axios.post(
            GRAPHQL_API_URL,
            JSON.stringify({ query, variables }),
            {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + token,
                }
            }
        );
        return apiResponse;
    })();
}

/**
 * 指定サービスのAI利用可能回数を取得
 * @param {string} service - サービス名
 * @param {string} month - 月（YYYY-MM形式）※後方互換性のため残すが使用されない
 * @returns {Promise<number>} - 利用可能回数
 */
export async function getAiUsageCount(service: string, month: string): Promise<number> {
    const logger = Log.getLogger();
    logger.api.info("Lantana-API", "Start to request: getAiUsageCount");

    const gql = `
        mutation($service: String!) {
            aiUsage(service: $service) {
                aiServiceName
                usageCount
            }
        }
    `;

    const resp = await query<AiUsageGraphQLResponse>(gql, { service });

    if (resp.data.errors && resp.data.errors.length > 0) {
        const reqid = getEwbReqId(resp);
        const msg = `Failed to execute getAiUsageCount API: id=${reqid}`;
        logger.api.error("Lantana-API", msg, resp.headers);
        throw { err: new Error(msg) };
    }

    logger.api.info("Lantana-API", "Succeed to request: getAiUsageCount", resp.headers);

    return resp.data.data.aiUsage?.usageCount ?? 0;
}

/**
 * 指定サービスのAI利用回数を更新（上書き）
 * @param {string} service - サービス名
 * @param {string} month - 月（後方互換性のため残すが使用されない）
 * @param {number} count - 利用回数
 * @returns {Promise<AiUsageResponse>} - 更新後の利用回数情報
 */
export async function updateAiUsageCount(
    service: string,
    month: string,
    count: number
): Promise<AiUsageResponse> {
    const logger = Log.getLogger();
    logger.api.info("Lantana-API", "Start to request: updateAiUsageCount");

    const gql = `
        mutation($input: UpdateAiUsageInput!) {
            updateAiUsage(input: $input) {
                aiServiceName
                month
                usageCount
            }
        }
    `;

    const variables = {
        input: {
            service,
            count
        }
    };

    const resp = await query<{ updateAiUsage: AiUsageResponse }>(gql, variables);

    if (resp.data.errors && resp.data.errors.length > 0) {
        const reqid = getEwbReqId(resp);
        const msg = `Failed to execute updateAiUsageCount API: id=${reqid}`;
        logger.api.error("Lantana-API", msg, resp.headers);
        throw { err: new Error(msg) };
    }

    logger.api.info("Lantana-API", "Succeed to request: updateAiUsageCount", resp.headers);

    return resp.data.data.updateAiUsage;
}

/**
 * Docs Insight Reportを作成する
 * @param {string[]} s3Uris - S3 URIの配列
 * @param {string} requestId - リクエストID
 * @returns {Promise<{ isSuccess: boolean; message: string; requestId: string }>} - レスポンス
 */
export async function createDocsInsightsReport(
    s3Uris: string[],
    requestId: string
): Promise<{ isSuccess: boolean; message: string; requestId: string }> {
    const logger = Log.getLogger();
    logger.api.info("Lantana-API", "Start to request: createDocsInsightsReport");

    const gql = `
        mutation($input: CreateDocsInsightsReportInput!) {
            createDocsInsightsReport(input: $input) {
                isSuccess
                message
                requestId
            }
        }
    `;

    const variables = {
        input: {
            s3Uris,
            requestId
        }
    };

    const resp = await query<{ createDocsInsightsReport: { isSuccess: boolean; message: string; requestId: string } }>(gql, variables);

    if (resp.data.errors && resp.data.errors.length > 0) {
        console.error(resp.data.errors);
        const reqid = getEwbReqId(resp);
        const msg = `Failed to execute createDocsInsightsReport API: id=${reqid}`;
        logger.api.error("Lantana-API", msg, resp.headers);
        throw { err: new Error(msg) };
    }

    logger.api.info("Lantana-API", "Succeed to request: createDocsInsightsReport", resp.headers);

    return resp.data.data.createDocsInsightsReport;
}

/**
 * 利用規約同意状況を取得する
 * @param {string} termsAndConditionsName - 利用規約名（例: "privacy_policy"）
 * @returns {Promise<{ termsAndConditionsName: string; termsAndConditionsVersion: string; updateAt: string }>} - 同意状況情報
 */
export async function getTermsAndConditionsAgreementStatus(
    termsAndConditionsName: string
): Promise<{ termsAndConditionsName: string; termsAndConditionsVersion: string; updateAt: string }> {
    const logger = Log.getLogger();
    logger.api.info("Lantana-API", "Start to request: getTermsAndConditionsAgreementStatus");

    const gql = `
        query GetTermsAndConditionsAgreementStatus($termsAndConditionsName: String!) {
            getTermsAndConditionsAgreementStatus(termsAndConditionsName: $termsAndConditionsName) {
                termsAndConditionsName
                termsAndConditionsVersion
                updateAt
            }
        }
    `;

    const variables = {
        termsAndConditionsName
    };

    const resp = await query<{
        getTermsAndConditionsAgreementStatus: {
            termsAndConditionsName: string;
            termsAndConditionsVersion: string;
            updateAt: string;
        }
    }>(gql, variables);

    if (resp.data.errors && resp.data.errors.length > 0) {
        const reqid = getEwbReqId(resp);
        const msg = `Failed to execute getTermsAndConditionsAgreementStatus API: id=${reqid}`;
        logger.api.error("Lantana-API", msg, resp.headers);
        throw { err: new Error(msg) };
    }

    logger.api.info("Lantana-API", "Succeed to request: getTermsAndConditionsAgreementStatus", resp.headers);

    return resp.data.data.getTermsAndConditionsAgreementStatus;
}

/**
 * 利用規約同意状況を追加する
 * 
 * ユーザが特定の利用規約に同意したことを記録するためのGraphQL Mutationを送信します。
 * 利用規約の名前とバージョンを含む同意情報を登録します。
 * 
 * @param termsAndConditionsName - 利用規約文書の名前（例: "プライバシーポリシー"）
 * @param termsAndConditionsVersion - 利用規約文書のバージョン（例: "v2.1"）
 * @returns 同意詳細情報を含むオブジェクトのPromise
 * @returns termsAndConditionsName - 同意した利用規約の名前
 * @returns termsAndConditionsVersion - 同意した利用規約のバージョン
 * @returns updateAt - 同意が記録されたISOタイムスタンプ
 * 
 * @throws {Error} GraphQL Mutationが失敗した場合やエラーが返された場合にエラーをスローします
 * 
 * @example
 * ```typescript
 * const agreement = await addTermsAndConditionsAgreementStatus(
 *   "プライバシーポリシー", 
 *   "v2.1"
 * );
 * console.log(agreement.updateAt); // "2023-12-07T10:30:00Z"
 * ```
 */
export async function addTermsAndConditionsAgreementStatus(
    termsAndConditionsName: string,
    termsAndConditionsVersion: string,
): Promise<{ termsAndConditionsName: string; termsAndConditionsVersion: string; updateAt: string }> {
    const logger = Log.getLogger();
    logger.api.info("Lantana-API", "Start to request: addTermsAndConditionsAgreementStatus");

    const gql = `
        mutation AddTermsAndConditionsAgreementStatus($input: AddTermsAndConditionsAgreementStatusInput!) {
            addTermsAndConditionsAgreementStatus(input: $input) {
                termsAndConditionsName
                termsAndConditionsVersion
                updateAt
            }
        }
    `;

    const variables = {
        input: {
            termsAndConditionsName,
            termsAndConditionsVersion,
        }
    };

    const resp = await query<{
        addTermsAndConditionsAgreementStatus: {
            termsAndConditionsName: string;
            termsAndConditionsVersion: string;
            updateAt: string;
        }
    }>(gql, variables);

    if (resp.data.errors && resp.data.errors.length > 0) {
        const reqid = getEwbReqId(resp);
        const msg = `Failed to execute addTermsAndConditionsAgreementStatus API: id=${reqid}`;
        logger.api.error("Lantana-API", msg, resp.headers);
        throw { err: new Error(msg) };
    }

    logger.api.info("Lantana-API", "Succeed to request: addTermsAndConditionsAgreementStatus", resp.headers);

    return resp.data.data.addTermsAndConditionsAgreementStatus;
}